import { e, type EventTuple } from '@/events/types'
import { rectangleVoxels } from '@/events/ops'
import { rasterizeShape } from '@/shapes'
import { writeFn, blendMutable } from '@/events/layerSystem'
import { posKeyXYZ, parseKeyX, parseKeyY, parseKeyZ } from '@/voxelMath'
import { isVoxelMaterial, type VoxelData, type VoxelMap, type Face } from '@/voxelTypes'
import { coerceHexColorNumber } from '@/utils/color'
import type { SceneDoc, Node, GroupNode, VoxelSpec, Mode, Pos } from './types'

type CompileResult = { map: VoxelMap }

const FACE_VALUES: ReadonlySet<Face> = new Set(['x+','x-','y+','y-','z+','z-'])

function toVoxelData(spec: VoxelSpec): VoxelData {
  if (typeof spec === 'string' || typeof spec === 'number') {
    const color = coerceHexColorNumber(spec)
    return color != null ? { type: 'solid', color } : { type: 'solid' }
  }
  const [type, colorMaybe, params] = spec
  const color = coerceHexColorNumber(colorMaybe as string | number | undefined)
  const v: VoxelData = color != null ? { type, color } : { type }
  if (params && typeof params === 'object') {
    const face = (params as Record<string, unknown>).face
    if (typeof face === 'string' && FACE_VALUES.has(face as Face)) {
      (v as any).face = face as Face
    }
    const material = (params as Record<string, unknown>).material
    if (isVoxelMaterial(material)) {
      v.material = material
    }
  }
  return v
}

function translateMap(map: VoxelMap, at: Pos): Map<number, VoxelData> {
  const [dx, dy, dz] = at
  const out = new Map<number, VoxelData>()
  if ((dx | dy | dz) === 0) {
    ;(map as any).forEach((v: VoxelData, k: number) => { out.set(k, v) })
  } else {
    ;(map as any).forEach((v: VoxelData, k: number) => {
      const nx = parseKeyX(k) + dx
      const ny = parseKeyY(k) + dy
      const nz = parseKeyZ(k) + dz
      out.set(posKeyXYZ(nx, ny, nz), v)
    })
  }
  return out
}

function buildVoxNode(vox: ReadonlyArray<readonly [number, number, number, VoxelSpec]>): VoxelMap {
  const out = new Map<number, VoxelData>()
  for (const entry of vox) {
    const [x, y, z, spec] = entry
    const v = toVoxelData(spec)
    out.set(posKeyXYZ(x, y, z), v)
  }
  return out
}

function buildShape(kind: 'sphere' | 'cylinder' | 'cone', params: {
  center: Pos; radius: number; height?: number; axis?: 'x'|'y'|'z'; voxel: VoxelSpec
}): VoxelMap {
  const out = new Map<number, VoxelData>()
  const voxel = toVoxelData(params.voxel)
  rasterizeShape(kind, { center: { x: params.center[0], y: params.center[1], z: params.center[2] }, radius: params.radius, height: params.height, axis: params.axis, voxel }, undefined, writeFn('place', out as unknown as VoxelMap))
  return out
}

function buildBox(min: Pos, max: Pos, voxel: VoxelSpec, mode: Mode = 'place'): VoxelMap {
  const out = new Map<number, VoxelData>()
  rectangleVoxels(
    { x: min[0], y: min[1], z: min[2] },
    { x: max[0], y: max[1], z: max[2] },
    toVoxelData(voxel),
    out as unknown as VoxelMap,
    mode === 'fill' ? 'fill' : (mode === 'subtract' ? 'subtract' : 'place')
  )
  return out
}

function composeGroup(node: GroupNode, defs: Record<string, Node>, visiting: Set<string>): CompileResult {
  const at = node.at ?? [0, 0, 0]
  let acc: VoxelMap = new Map<number, VoxelData>()
  for (const child of node.children) {
    const res = buildNodeInternal(child, defs, visiting)
    const translated = translateMap(res.map, at)
    const mode: Mode = (child as any).mode || 'place'
    acc = blendMutable(acc, translated, mode)
  }
  return { map: acc }
}

function ensureArrayNodes(v: Node | ReadonlyArray<Node>): ReadonlyArray<Node> {
  return (Array.isArray(v) ? v : [v]) as ReadonlyArray<Node>
}

function buildNodeInternal(node: Node, defs: Record<string, Node>, visiting: Set<string>): CompileResult {
  switch (node.kind) {
    case 'vox':
      return { map: buildVoxNode(node.vox) }
    case 'box':
      return { map: buildBox(node.min, node.max, node.voxel, node.mode || 'place') }
    case 'sphere':
      return { map: buildShape('sphere', { center: node.center, radius: node.radius, voxel: node.voxel }) }
    case 'cylinder':
      return { map: buildShape('cylinder', { center: node.center, radius: node.radius, height: node.height, axis: node.axis, voxel: node.voxel }) }
    case 'cone':
      return { map: buildShape('cone', { center: node.center, radius: node.radius, height: node.height, axis: node.axis, voxel: node.voxel }) }
    case 'group':
      return composeGroup(node, defs, visiting)
    case 'def': {
      // Definitions are materialized only when referenced via 'use'
      return buildNodeInternal(node.node, defs, visiting)
    }
    case 'use': {
      const name = node.ref
      const target = defs[name]
      if (!target) return { map: new Map() }
      if (visiting.has(name)) {
        // Avoid cycles; skip
        return { map: new Map() }
      }
      visiting.add(name)
      const built = buildNodeInternal(target, defs, visiting)
      visiting.delete(name)
      const translated = node.at ? translateMap(built.map, node.at) : built.map
      return { map: translated }
    }
    case 'subtract': {
      const left = buildNodeInternal(node.left, defs, visiting).map
      const rights = ensureArrayNodes(node.right).map((r) => buildNodeInternal(r, defs, visiting).map)
      let acc = left
      for (const r of rights) acc = blendMutable(acc, r, 'subtract')
      return { map: acc }
    }
  }
}

export function compileSceneDsl(doc: SceneDoc | Node | ReadonlyArray<Node>): { events: EventTuple[]; meta?: SceneDoc['meta'] } {
  const nodes: ReadonlyArray<Node> = Array.isArray(doc)
    ? doc
    : (doc as any).kind
      ? [doc as Node]
      : (doc as SceneDoc).scene && Array.isArray((doc as SceneDoc).scene)
        ? (doc as SceneDoc).scene as ReadonlyArray<Node>
        : [(doc as SceneDoc).scene as Node]

  const defs: Record<string, Node> = (Array.isArray(doc) || (doc as any).kind) ? {} : Object((doc as SceneDoc).defs)
  const events: EventTuple[] = []
  for (const n of nodes) {
    const built = buildNodeInternal(n, defs, new Set())
    const mode: Mode = (n as any).mode || 'place'
    if (mode === 'subtract') {
      // subtract expects a list of keys only
      const keys = Array.from(built.map.keys())
      events.push(e(['voxel.blend', keys, 'subtract']))
    } else if (mode === 'fill') {
      events.push(e(['voxel.blend', built.map, 'fill']))
    } else if (mode === 'mask' || mode === 'xor' || mode === 'paint') {
      events.push(e(['voxel.blend', built.map, mode]))
    } else {
      events.push(e(['voxel.blend', built.map, 'place']))
    }
  }

  const meta = (Array.isArray(doc) || (doc as any).kind) ? undefined : (doc as SceneDoc).meta
  return { events, meta }
}
